import { KLineData } from "klinecharts";
import { klineLineStyle, lineStyleToKLine } from "./lineStyle";
import { getPriceValue, readStyle, shiftResults } from "./indicatorUtils";

export const createEnvelopes = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const period = (values.Period as number) ?? 14;
  const shift = (values.Shift as number) ?? 0;
  const deviation = (values.Deviation as number) ?? 0.1;
  const method = (values.Method as string) ?? "Simple";
  const applyTo = (values["Apply to"] as string) ?? "Close";

  const upperS = readStyle(styles, "upper", "#2196F3");
  const middleS = readStyle(styles, "middle", "#FF9600");
  const lowerS = readStyle(styles, "lower", "#2196F3");

  return {
    name: "envelopes",
    shortName: `ENV${period}`,
    calcParams: [period, shift, deviation, method, applyTo],
    series: "price" as const,
    figures: [
      { key: "upper", title: "Upper: ", type: "line" },
      { key: "middle", title: "Middle: ", type: "line" },
      { key: "lower", title: "Lower: ", type: "line" },
    ],
    styles: {
      lines: [
        klineLineStyle(upperS.color, 1, lineStyleToKLine(upperS.style)),
        klineLineStyle(middleS.color, 2, lineStyleToKLine(middleS.style)),
        klineLineStyle(lowerS.color, 1, lineStyleToKLine(lowerS.style)),
      ],
    },
    regenerateFigures: () => [
      { key: "upper", title: "Upper: ", type: "line" },
      { key: "middle", title: "Middle: ", type: "line" },
      { key: "lower", title: "Lower: ", type: "line" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [p, , devPct, maMethod, priceField] = (indicator.calcParams ?? [
        period,
        shift,
        deviation,
        method,
        applyTo,
      ]) as [number, number, number, string, string];
      const n = dataList.length;
      const results: Array<{ upper?: number; middle?: number; lower?: number }> =
        new Array(n);
      const devFactor = devPct / 100;

      let smaSum = 0;
      let prevEma = 0;
      let prevSmoothed = 0;
      const lwBuffer: number[] =
        maMethod === "Linear Weighted" ? new Array(p) : [];
      let lwIndex = 0;
      let lwCount = 0;
      const expMultiplier = 2 / (p + 1);
      const smoothedAlpha = 1 / p;
      const lwWeightSum = (p * (p + 1)) / 2;

      for (let i = 0; i < n; i++) {
        const priceValue = getPriceValue(dataList[i]!, priceField);
        let ma: number | undefined;

        if (maMethod === "Simple") {
          smaSum += priceValue;
          if (i >= p) smaSum -= getPriceValue(dataList[i - p]!, priceField);
          if (i >= p - 1) ma = smaSum / p;
        } else if (maMethod === "Exponential") {
          if (i === 0) prevEma = priceValue;
          else prevEma = (priceValue - prevEma) * expMultiplier + prevEma;
          if (i >= p - 1) ma = prevEma;
        } else if (maMethod === "Smoothed") {
          if (i === 0) prevSmoothed = priceValue;
          else
            prevSmoothed =
              smoothedAlpha * priceValue + (1 - smoothedAlpha) * prevSmoothed;
          if (i >= p - 1) ma = prevSmoothed;
        } else if (maMethod === "Linear Weighted") {
          lwBuffer[lwIndex] = priceValue;
          lwIndex = (lwIndex + 1) % p;
          if (lwCount < p) lwCount += 1;
          if (lwCount === p) {
            let weightedSum = 0;
            for (let k = 0; k < p; k++) {
              weightedSum += lwBuffer[(lwIndex + k) % p]! * (k + 1);
            }
            ma = weightedSum / lwWeightSum;
          }
        } else {
          smaSum += priceValue;
          if (i >= p) smaSum -= getPriceValue(dataList[i - p]!, priceField);
          if (i >= p - 1) ma = smaSum / p;
        }

        if (ma !== undefined) {
          const band = ma * devFactor;
          results[i] = { middle: ma, upper: ma + band, lower: ma - band };
        } else {
          results[i] = {};
        }
      }

      const [, shiftParam] = (indicator.calcParams ?? []) as [number, number];
      return shiftParam !== 0 ? shiftResults(results, shiftParam) : results;
    },
  };
};
